<template>
    <div>
        <form>
            <div
                class="multiselect"
                :class="`multiselect--${prefferedOpenDirection}`"
            >

                <div
                    class="multiselect__form"
                    @click="handleMultiselectFormClick">
                    <span
                        :id="'autocomplete-label-' + instanceId"
                        class="sr-only">
                    </span>

                    <div
                        :aria-expanded="isSuggestionBoxShown ? 'true' : 'false'"
                        :aria-owns="'autocomplete-listbox-' + instanceId"
                        aria-haspopup="listbox"
                        :id="'autocomplete-combobox-' + instanceId"
                        role="combobox"
                        class="multiselect__input-wrapper"
                        @blur.prevent="hideSuggestions()"
                    >
                        <input
                            :data-label="accessibilityLabel"
                            :name="name"
                            :required="(required) ? true : false"
                            type="text"
                            :id="id"
                            class="field__input field--text__input"
                            :class="{'field__input--open' : isSuggestionBoxShown, 'field__input--error': !isValid}"
                            aria-autocomplete="list"
                            :aria-controls="'autocomplete-listbox-' + instanceId"
                            :aria-labelledby="'autocomplete-label-' + instanceId"
                            :aria-activedescendant="ariaActiveDescendant"
                            :placeholder="internalPlaceholder"
                            v-model="query"
                            @keyup.esc="clearInput()"
                            @click="showSuggestions()"
                            @focus="handleFocus()"
                            @blur.prevent="handleBlur"
                            @input="changeQuery($event.target.value)"
                            @keydown.right="cleanAriaActiveDescendant"
                            @keydown.left="cleanAriaActiveDescendant"
                            @keydown.down.prevent="moveHighlightDown()"
                            @keydown.up.prevent="moveHighlightUp()"
                            @keydown.enter.prevent.stop.self="selectHighlightedSuggestion()"
                        >
                    </div>
                </div>

                <div
                    :id="'autocomplete-announce-' + instanceId"
                    class="sr-only"
                    aria-live="assertive">
                    <template v-if="flattenedSuggestions.ids.length > 0 && isSuggestionBoxShown">
                        {{ trans("x_suggestions_found", flattenedSuggestions.ids.length) }}.
                    </template>
                </div>
                <div
                    v-show="isSuggestionBoxShown"
                    class="multiselect__content-wrapper"
                    :style="{ maxHeight: optimizedHeight + 'px' }"
                    ref="list">
                    <ul
                        :id="'autocomplete-listbox-' + instanceId"
                        class="multiselect__content"
                        :class="{ hasSuggestionGroups : 'multiselect__option-list'}"
                        :aria-labelledby="'autocomplete-label-' + instanceId"
                        :aria-label="trans('suggestions')"
                        role="listbox"
                        @mousedown.prevent
                    >
                        <template
                            v-if="hasSuggestionGroups">
                            <li
                                v-for="(group, groupIndex) in internalSuggestions"
                                :key="group.group">
                                <div class="multiselect__option multiselect__option--disabled">{{ group.group }}</div>
                                <ul
                                    class="multiselect__option-list"
                                    :aria-label="group.group">
                                    <li
                                        v-for="(suggestion, index) in group.results"
                                        :aria-selected="hightlightedOptionId === 'autocomplete-' + instanceId + '-group-' + groupIndex + '-option-' + index ? 'true' : 'false'"
                                        :key="suggestion.title"
                                        role="option"
                                        :id="'autocomplete-' + instanceId + '-group-' + groupIndex + '-option-' + index"
                                        class="multiselect__option"
                                        :class="{'multiselect__option--highlight' : hightlightedOptionId === 'autocomplete-' + instanceId + '-group-' + groupIndex + '-option-' + index }"
                                        @mouseenter.self="setHighlight('autocomplete-' + instanceId + '-group-' + groupIndex + '-option-' + index)"
                                        @click.stop="selectSuggestion(suggestion)">
                                        {{ suggestion.title }}
                                        <span class="sr-only">{{ setNumbering(groupIndex, index) }}</span>
                                    </li>
                                </ul>
                            </li>
                        </template>
                        <template v-else>
                            <li
                                v-for="(suggestion, index) in suggestions"
                                :aria-selected="hightlightedOptionId === 'autocomplete-' + instanceId + '-option-' + index ? 'true' : 'false'"
                                :key="suggestion.title"
                                role="option"
                                :id="'autocomplete-' + instanceId + '-option-' + index"
                                class="multiselect__option"
                                :class="{'multiselect__option--highlight' : hightlightedOptionId === 'autocomplete-' + instanceId + '-option-' + index }"
                                @mouseenter.self="setHighlight('autocomplete-' + instanceId + '-option-' + index)"
                                @click.stop="selectSuggestion(suggestion)">
                                {{ suggestion.title }}
                                <span class="sr-only">{{ setNumbering(0, index) }}</span>
                            </li>
                        </template>
                    </ul>
                </div>
                <div
                    :id="'autocomplete-selected-' + instanceId"
                    aria-live="assertive"
                    class="sr-only">
                    {{ (newlyAddedSuggestion !== null && newlyAddedSuggestion.title !== "") ? newlyAddedSuggestion.title + " " + trans("has_been_added") : "" }}
                </div>
            </div>
        </form>
    </div>
</template>

<script>

export default {
    props: {
        suggestions: {
            type: Array,
            required: false,
            default: () => []
        },
        placeholder: {
            type: String,
            required: false,
            default: () => ""
        },
        multipleValuesPlaceholder: {
            type: String,
            required: false,
            default: ""
        },
        accessibilityLabel: {
            type: String,
            required: true
        },
        instanceId: {
            type: String,
            required: true
        },
        isLoading: {
            type: Boolean,
            required: false,
            default: false
        },
        isInputClearedAfterSelection: {
            type: Boolean,
            required: false,
            default: true
        },
        hasSuggestionGroups: {
            type: Boolean,
            required: false,
            default: false
        },
        maxHeight: {
            type: Number,
            required: false,
            default: 240
        },
        defaultSelectedValue: {
            type: Object,
            required: false,
            default() {
                return {};
            }
        },
        hideSuggestionBox: {
            type: Boolean,
            required: false,
            default: true
        },
        hasTagsListMaxHeight: {
            type: Boolean,
            required: false,
            default: false
        },
        required: {
            type: Boolean,
            required: false,
            default: false
        },
        name: {
            type: String,
            required: true
        },
        isValid: {
            type: Boolean,
            required: false,
            default: true
        },
        id: {
            type: String,
            required: true
        }
    },
    data() {
        return {
            query: "",
            selected: [],
            newlyAddedSuggestion: null,
            internalSuggestions: [],
            isSuggestionBoxShown: false,
            hightlightedOptionId: null,
            ariaActiveDescendant: null,
            optimizedHeight: this.maxHeight,
            prefferedOpenDirection: "below",
            internalPlaceholder: this.placeholder,
            autoCompleteInput: null,
            isFocused: false,
        };
    },
    computed: {
        flattenedSuggestions: function() {
            var suggestionIds = [];
            var suggestions = [];

            if (this.hasSuggestionGroups) {
                this.internalSuggestions.forEach((group, groupIndex) => {
                    group.results.forEach((suggestion, index) => {
                        suggestionIds.push("autocomplete-" + this.instanceId + "-group-" + groupIndex + "-option-" + index);
                        suggestions.push(suggestion);
                    });
                });
            } else {
                this.internalSuggestions.forEach((suggestion, index) => {
                    suggestionIds.push("autocomplete-" + this.instanceId + "-option-" + index);
                    suggestions.push(suggestion);
                });
            }
            return {"ids": suggestionIds, "suggestions": suggestions};
        },
    },
    watch: {
        defaultSelectedValue: {
            handler: function(val, oldVal) {
                this.selected = val;
                if (this.selected) {
                    this.query = this.selected.title;
                } else {
                    this.query = "";
                }
            },
            immediate: true
        },
        suggestions: function(val, oldVal) {
            this.internalSuggestions = val;

            if (val.length > 0 && this.isFocused) {
                this.setHighlight(this.flattenedSuggestions.ids[0]);
                this.isSuggestionBoxShown = true;
            }

        },
        hideSuggestionBox: function(val) {
            this.isSuggestionBoxShown = val;
        },
        hightlightedOptionId: function(val, oldVal) {
            let hasPreselection = val ? true : false;
            this.$emit("preselect", val);
        }
    },
    mounted: function() {
        if (this.defaultSelectedValue) {
            this.selected = this.defaultSelectedValue;
            this.query = this.selected.title;
            this.updatePlaceholder();
        }

        this.autocompleteInput = document.getElementById(this.id);
    },

    methods: {
        resetSuggestions: function() {
            this.internalSuggestions = [];
        },
        changeQuery: function(value) {
            this.$emit("search-change", value);
        },
        selectSuggestion: function(suggestion) {
            this.query = suggestion.title;
            this.newlyAddedSuggestion = suggestion;

            this.$emit("input", suggestion);

            this.isSuggestionBoxShown = false;

            this.updatePlaceholder();

            if (this.isInputClearedAfterSelection) {
                this.query = "";
            }


            this.resetSuggestions();
        },
        selectHighlightedSuggestion: function() {
            if (this.flattenedSuggestions.ids.length) {
                // find highlighted ID in list of suggestions
                var index = this.flattenedSuggestions.ids.indexOf(this.hightlightedOptionId);
                var suggestion = this.flattenedSuggestions.suggestions[index];
                // put in in selected
                this.selectSuggestion(suggestion);
            }
        },
        clearInput: function() {
            this.query = "";
            this.hightlightedOptionId = null;
            this.resetSuggestions();
            this.hideSuggestions();
        },
        hideSuggestions: function() {
            if (this.suggestions.length == 0) {
                this.resetSuggestions();
                this.$emit("input", null);
            }

            this.isSuggestionBoxShown = false;
        },
        showSuggestions: function() {
            this.adjustPosition();

            if (this.internalSuggestions.length && this.isFocused) {
                this.isSuggestionBoxShown = true;
            }
        },
        setHighlight: function(elementId) {
            this.hightlightedOptionId = elementId;
            this.setAriaActiveDescendant(elementId);
        },
        moveHighlightUp: function() {
            if (!this.hightlightedOptionId) {
                return;
            }

            this.showSuggestions();

            var currentIndex = this.flattenedSuggestions.ids.indexOf(this.hightlightedOptionId);

            const hightlightedOption = document.getElementById(this.hightlightedOptionId);

            if (hightlightedOption) {
                if (currentIndex < 1) {
                    this.setHighlight(this.flattenedSuggestions.ids[this.flattenedSuggestions.ids.length - 1]);

                    this.$refs.list.scrollTop = hightlightedOption.offsetTop;
                } else {
                    this.setHighlight(this.flattenedSuggestions.ids[currentIndex - 1]);
                }

                const hightlightedOption = document.getElementById(this.hightlightedOptionId);

                const hightlightedOptionTop = hightlightedOption.offsetTop;
                const hightlightedOptionHeight = hightlightedOption.offsetHeight;

                if ((hightlightedOptionTop - hightlightedOptionHeight) <= this.$refs.list.scrollTop) {
                    this.$refs.list.scrollTop = this.$refs.list.scrollTop - hightlightedOptionHeight;
                }
            }

        },
        moveHighlightDown: function() {
            if (!this.hightlightedOptionId) {
                return;
            }

            this.showSuggestions();

            var currentIndex = this.flattenedSuggestions.ids.indexOf(this.hightlightedOptionId);

            if (currentIndex >= this.flattenedSuggestions.ids.length - 1) {
                this.setHighlight(this.flattenedSuggestions.ids[0]);
                this.$refs.list.scrollTop = 0;
            } else {
                this.setHighlight(this.flattenedSuggestions.ids[currentIndex + 1]);
            }

            this.setAriaActiveDescendant(this.hightlightedOptionId);

            const hightlightedOption = document.getElementById(this.hightlightedOptionId);

            if (hightlightedOption) {
                const hightlightedOptionTop = hightlightedOption.offsetTop;
                const hightlightedOptionHeight = hightlightedOption.offsetHeight;
                const optionListHeight = this.$refs.list.offsetHeight;

                if ((hightlightedOptionTop + hightlightedOptionHeight) >= optionListHeight) {
                    this.$refs.list.scrollTop = this.$refs.list.scrollTop + hightlightedOptionHeight;
                }
            }

        },
        adjustPosition () {
            if (typeof window === "undefined") return;

            const spaceAbove = this.$el.getBoundingClientRect().top;
            const spaceBelow = window.innerHeight - this.$el.getBoundingClientRect().bottom;

            const hasEnoughSpaceBelow = spaceBelow > this.maxHeight;

            if (hasEnoughSpaceBelow || spaceBelow > spaceAbove) {
                this.prefferedOpenDirection = "below";
                this.optimizedHeight = Math.min(spaceBelow - 40, this.maxHeight);
            } else {
                this.prefferedOpenDirection = "above";
                this.optimizedHeight = Math.min(spaceAbove - 40, this.maxHeight);
            }
        },
        setAriaActiveDescendant: function(val) {
            this.ariaActiveDescendant = val;
        },
        cleanAriaActiveDescendant: function() {
            this.ariaActiveDescendant = "";
        },
        handleFocus() {
            this.showSuggestions();
            this.$emit("focus");
            this.isFocused = true;
        },
        handleBlur(event) {
            this.isFocused = false;
            this.$emit("blur");
            this.selectHighlightedSuggestion();
            this.hideSuggestions();

        },
        setNumbering: function(groupIndex, index) {
            let numbering = 0;
            if(groupIndex !== 0) {
                for(let i = 0 ; i < groupIndex; i++) {
                    numbering +=  this.suggestions[i].results.length;
                }
            }

            return  this.trans("x_of_x", [numbering + index + 1, this.flattenedSuggestions.ids.length]);
        },
        refresh: function() {
            this.$emit("refresh");
        },
        removeValueFromSelected: function(value) {
            var storeModule = "searchModule";

            var valueIndex = this.getObjetIndexByKey(this.selected, "queryValue", value.queryValue);
            if (valueIndex !== -1) {
                this.selected.splice(valueIndex, 1);
                this.updatePlaceholder();
            }

            this.autocompleteInput.focus();

            this.refresh();
        },
        updatePlaceholder: function() {
            if (this.selected.length > 0) {
                if (this.multipleValuesPlaceholder.length > 0) {
                    this.internalPlaceholder = this.multipleValuesPlaceholder;
                }
            } else {
                this.internalPlaceholder = this.placeholder;
            }
        },
        handleMultiselectFormClick: function() {
            this.autocompleteInput.focus();
        },
    }
};
</script>
